import React, { useState } from 'react';
import { Building2, HardHat, ArrowRight, X } from 'lucide-react';
import { api } from '../lib/api';
import { useTrades } from '../hooks/useTrades';
import { MultiSelectDropdown } from './shared/MultiSelectDropdown';

interface OnboardingProps {
  userId: string;
  onComplete: () => void;
}

export const Onboarding: React.FC<OnboardingProps> = ({ userId, onComplete }) => {
  const { trades, loading: tradesLoading } = useTrades();
  const [step, setStep] = useState<'role' | 'details'>('role');
  const [selectedRole, setSelectedRole] = useState<'contractor' | 'gc' | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    phone: '',
    location: '',
  });
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const handleRoleSelect = (role: 'contractor' | 'gc') => {
    setSelectedRole(role);
  };

  const handleContinueToDetails = () => {
    if (selectedRole) {
      setStep('details');
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await api.patch('/profiles/me', {
        role: selectedRole,
        onboardingCompleted: true,
      });

      // Wait for profile to be refreshed before proceeding
      // This will update the profile state, causing App.tsx to re-render
      // and automatically show the dashboard (since onboardingCompleted will be true)
      await onComplete();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      // You might want to show an error message to the user here
    } finally {
      setLoading(false);
    }
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true;

    const phoneRegex = /^[\d\s\-\(\)\+\.]+$/;
    const digitsOnly = phone.replace(/\D/g, '');

    if (!phoneRegex.test(phone)) {
      setPhoneError('Phone number can only contain numbers, spaces, dashes, parentheses, and plus signs');
      return false;
    }

    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      setPhoneError('Phone number must be between 10 and 15 digits');
      return false;
    }

    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (value: string) => {
    setFormData({ ...formData, phone: value });
    if (value) {
      validatePhone(value);
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhone(formData.phone)) {
      return;
    }

    setLoading(true);

    try {
      const updateData: Record<string, unknown> = {
        role: selectedRole,
        fullName: formData.fullName || null,
        companyName: formData.companyName || null,
        phone: formData.phone || null,
        location: formData.location || null,
        onboardingCompleted: true,
      };

      if (selectedRole === 'contractor' && selectedTrades.length > 0) {
        updateData.trades = selectedTrades;
      }

      await api.patch('/profiles/me', updateData);

      // Wait for profile to be refreshed before proceeding
      // This will update the profile state, causing App.tsx to re-render
      // and automatically show the dashboard (since onboardingCompleted will be true)
      await onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // You might want to show an error message to the user here
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>

      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-2xl w-full p-8 md:p-12 border border-white/20 relative z-10">
        {step === 'role' ? (
          <div className="space-y-8">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome to VendorBoom!</h1>
              <p className="text-slate-600">Let's get you set up. How will you be using VendorBoom?</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={() => handleRoleSelect('contractor')}
                className={`p-8 rounded-xl border-2 transition-all ${
                  selectedRole === 'contractor'
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-white shadow-xl shadow-blue-500/20'
                    : 'border-slate-200 hover:border-blue-300 hover:shadow-lg'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center transition-all ${
                  selectedRole === 'contractor'
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg'
                    : 'bg-slate-100'
                }`}>
                  <HardHat className={`w-7 h-7 ${
                    selectedRole === 'contractor' ? 'text-white' : 'text-slate-600'
                  }`} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">I'm a Contractor</h3>
                <p className="text-sm text-slate-600">
                  Find jobs, manage insurance, and grow your business
                </p>
              </button>

              <button
                onClick={() => handleRoleSelect('gc')}
                className={`p-8 rounded-xl border-2 transition-all ${
                  selectedRole === 'gc'
                    ? 'border-amber-500 bg-gradient-to-br from-amber-50 to-white shadow-xl shadow-amber-500/20'
                    : 'border-slate-200 hover:border-amber-300 hover:shadow-lg'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center transition-all ${
                  selectedRole === 'gc'
                    ? 'bg-gradient-to-br from-amber-500 to-amber-600 shadow-lg'
                    : 'bg-slate-100'
                }`}>
                  <Building2 className={`w-7 h-7 ${
                    selectedRole === 'gc' ? 'text-white' : 'text-slate-600'
                  }`} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">I'm Hiring</h3>
                <p className="text-sm text-slate-600">
                  Post jobs, find contractors, and manage compliance
                </p>
              </button>
            </div>

            <button
              onClick={handleContinueToDetails}
              disabled={!selectedRole}
              className={`w-full text-white py-4 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] ${
                selectedRole === 'contractor'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                  : selectedRole === 'gc'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                  : 'bg-slate-400'
              }`}
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Tell us about yourself</h2>
                <p className="text-slate-600 mt-1">Help us personalize your experience (optional)</p>
              </div>
              <button
                onClick={handleSkip}
                disabled={loading}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className={`w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:ring-4 focus:border-transparent transition-all hover:border-slate-300 hover:shadow-md ${
                    selectedRole === 'contractor' ? 'focus:ring-blue-100 focus:border-blue-500' : 'focus:ring-amber-100 focus:border-amber-500'
                  }`}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className={`w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:ring-4 focus:border-transparent transition-all hover:border-slate-300 hover:shadow-md ${
                    selectedRole === 'contractor' ? 'focus:ring-blue-100 focus:border-blue-500' : 'focus:ring-amber-100 focus:border-amber-500'
                  }`}
                  placeholder="ABC Construction"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-4 focus:border-transparent transition-all hover:shadow-md ${
                    phoneError
                      ? 'border-red-300 focus:ring-red-100 focus:border-red-500'
                      : selectedRole === 'contractor'
                      ? 'border-slate-200 hover:border-slate-300 focus:ring-blue-100 focus:border-blue-500'
                      : 'border-slate-200 hover:border-slate-300 focus:ring-amber-100 focus:border-amber-500'
                  }`}
                  placeholder="(555) 123-4567"
                />
                {phoneError && (
                  <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className={`w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:ring-4 focus:border-transparent transition-all hover:border-slate-300 hover:shadow-md ${
                    selectedRole === 'contractor' ? 'focus:ring-blue-100 focus:border-blue-500' : 'focus:ring-amber-100 focus:border-amber-500'
                  }`}
                  placeholder="New York, NY"
                />
              </div>

              {selectedRole === 'contractor' && (
                <div>
                  <MultiSelectDropdown
                    options={trades.map(trade => ({
                      id: trade.id,
                      name: trade.name,
                      category: trade.category
                    }))}
                    selected={selectedTrades}
                    onChange={setSelectedTrades}
                    label="Select Your Trades"
                    placeholder={tradesLoading ? 'Loading trades...' : 'Select one or more trades (optional)'}
                    disabled={tradesLoading}
                    groupByCategory
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    This helps us match you with relevant jobs
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={loading}
                  className="flex-1 py-3 px-4 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 hover:border-slate-400 transition-all disabled:opacity-50 hover:shadow-md"
                >
                  Skip for now
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 text-white py-3 px-4 rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02] ${
                    selectedRole === 'contractor'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                      : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                  }`}
                >
                  {loading ? 'Saving...' : 'Complete Setup'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};